import { useEffect, useMemo, useState } from 'react';
import { isTrueScale, jobExtents, meshStamp } from './threeDApi';
import { mmScale } from './meshAnnotations';
import { builtBoxOf } from './openaiBlenderApi';
import { useCatalogModels } from './partModels';
import type { Dims } from './meshAnnotations';

// WHAT A PART'S 3D MODEL ACTUALLY MEASURES, in millimetres.
//
// Not what the manual printed about the part — that is `size`, and for a piece of hardware it
// is a designation ("M6, slotted drive") rather than three numbers. The mesh has three
// numbers, always: it is a shape, it has a bounding box, and the viewer has been labelling
// them on screen for as long as it has existed. Nothing outside that viewer could read them.
//
// TWO STEPS, because the box is stored unscaled. The server measures the glTF's own accessors
// (see the extents route) and answers in FILE units; the millimetres are the caller's
// arithmetic, out of mmScale — a CAD or lathe mesh carries its real size in the file, and
// everything else is anchored by what the manual printed. Same two lines the mesh viewer runs.
// `job` and `at`: which mesh, at which version (meshStamp), the numbers were read off.
export type MeshDims = { x: number; y: number; z: number; job: number; at: number };

// The app's axes: x across, y the depth, z up. Written out because "10 × 10 × 16" says
// nothing about which 16 is, and the whole reason to show these is that somebody wants to
// know how long the thing is.
export const formatMeshDims = (d: MeshDims): string =>
  `w ${Math.round(d.x)} × d ${Math.round(d.y)} × h ${Math.round(d.z)} mm`;

export const longestMesh = (d: MeshDims): number => Math.max(d.x, d.y, d.z);

// WHICH AXIS A LENGTH LANDS ON. A length read off a drawing is a number with no axis in it:
// it says how long the thing is and nothing about which way the mesh points, and a
// screwdriver points wherever the file put it.
//
// It needs no model to answer. mmPerUnit anchors a length against the mesh's LONGEST extent
// — that is the whole rule, and it is right by construction for anything longer than it is
// thick — so the axis the length becomes is the axis that extent lies on. Reading it off the
// same three numbers means the button says exactly what the viewer will do.
export const longestAxis = (d: MeshDims): 'x' | 'y' | 'z' => {
  if (d.x >= d.y && d.x >= d.z) return 'x';
  return d.y >= d.z ? 'y' : 'z';
};

// Catalog item -> its curated mesh's size. `want` names the items to measure and the printed
// size each of them would be anchored by; anything with no mesh, no extents or no anchor is
// simply absent, which is the answer "this cannot be measured" and not a zero.
export function useMeshDims(want: { itemId: number; dims: Dims }[]): Map<number, MeshDims> {
  const { models } = useCatalogModels();
  // NEWEST FIRST is how the endpoint answers, so the first entry for an item is the most
  // recent one. A Map built straight from the list would keep the LAST, which is the oldest
  // — the opposite of what anybody means by "the part's model".
  const newest = useMemo(() => {
    const out = new Map<number, (typeof models)[number]>();
    for (const m of models) {
      const at = m.itemId as number;
      if (at != null && !out.has(at)) out.set(at, m);
    }
    return out;
  }, [models]);

  const [dims, setDims] = useState<Map<number, MeshDims>>(new Map());
  const key = JSON.stringify(
    want.map((w) => [w.itemId, w.dims?.x, w.dims?.y, w.dims?.z, w.dims?.lengthMm]),
  );
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const out = new Map<number, MeshDims>();
      for (const w of want) {
        const job = newest.get(w.itemId);
        if (!job) continue;
        const at = meshStamp(job);
        const extent = await jobExtents(job.id, at);
        if (!extent) continue;
        // The run's own measured box last, for a part the manual never dimensioned — see
        // mmScale, which asks the drawing first.
        const k = mmScale(extent, w.dims, isTrueScale(job), builtBoxOf(job));
        if (!k) continue;
        out.set(w.itemId, { x: extent.x * k, y: extent.y * k, z: extent.z * k, job: job.id, at });
      }
      if (!cancelled) setDims(out);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, newest]);
  return dims;
}
